import React from 'react'
import IntroductoryIcon from '../../../../assets/codicons/introductory.svg'
import BeginnerIcon from '../../../../assets/codicons/beginner.svg'
import ModerateIcon from '../../../../assets/codicons/moderate.svg'
import IntermediateIcon from '../../../../assets/codicons/intermediate.svg'
import AdvancedIcon from '../../../../assets/codicons/advanced.svg'

import IntensityOneIcon from '../../../../assets/codicons/intensity-1.svg'
import IntensityTwoIcon from '../../../../assets/codicons/intensity-2.svg'
import IntensityThreeIcon from '../../../../assets/codicons/intensity-3.svg'
import IntensityFourIcon from '../../../../assets/codicons/intensity-4.svg'

export function getFilteredItemLabels(options) {
  const { filteredAttributes, excludedFilters, categoryRelationships } = options

  let filteredLabels = {}
  for (const attribute in filteredAttributes) {
    if (!excludedFilters.includes(attribute)) {
      let label
      switch (attribute) {
        case 'duration':
          label = durationLabels(filteredAttributes.duration)
          break
        case 'instructors':
          label = instructorLabels(filteredAttributes.instructors)
          break
        case 'styles':
          label = styleLabels(filteredAttributes.styles, categoryRelationships)
          break
        case 'difficulties':
          label = difficultyLabels(filteredAttributes.difficulties)
          break
        case 'intensities':
          label = intensityLabels(filteredAttributes.intensities)
          break
        case 'yoga':
        case 'fitness':
        case 'mindfulness':
        case 'wellness':
        case 'skills':
          label = otherLabels(filteredAttributes[attribute])
          break
        case 'equipment':
        case 'focus_area':
          label = equipmentFocusAreaLabels(filteredAttributes[attribute])
          break
      }
      filteredLabels[attribute] = label
    }
  }
  return filteredLabels
}

const durationLabels = (durationFilter) => {
  return durationFilter.map((duration) => {
    const durationValues = duration.split('-')
    const minDuration = durationValues[0]
    const maxDuration = durationValues[1]
    let display = `${minDuration}${
      maxDuration != 'null' ? ` - ${maxDuration}` : '+'
    }`
    return { text: `${display} mins`, meta: duration }
  })
}

const instructorLabels = (instructorFilter) =>
  instructorFilter.map((instructor) => ({
    text: `${instructor.user.firstName} ${instructor.user.lastName}`,
    meta: instructor.slug
  }))

const styleLabels = (stylesFilter, categoryRelationships) => {
  const filteredLabels = []
  for (const slug in stylesFilter) {
    const category = stylesFilter[slug]
    const filters = category.filteredSubcategories || []
    if (filters === 'all') {
      filteredLabels.push({
        text: category.title,
        meta: category.slug,
        options: { isRoot: true, rootSlug: slug }
      })
    } else {
      filters.forEach((subcategory) => {
        let text = ''
        if (!subcategory.slug) {
          return
        }
        const numberOfParentCategories = categoryRelationships
          .filter((categorySubcategory) => {
            const subcategorySlug =
              categorySubcategory.toPlanSubcategoryId.split('_')[1]

            const subcategoryToCompareSlug = subcategory.slug.split('_')[1]
            return subcategorySlug === subcategoryToCompareSlug
          })
          .count()
        if (numberOfParentCategories > 1) {
          text = `${category.title}:`
        }
        text += subcategory.title

        filteredLabels.push({
          text,
          meta: subcategory.slug,
          options: {
            isRoot: false,
            rootSlug: slug
          }
        })
      })
    }
  }
  return filteredLabels
}

const difficultyLabels = (difficultyFilter) =>
  difficultyFilter.map((difficulty) => ({
    text: difficulty,
    meta: difficulty
  }))

const intensityLabels = (intensityFilter) =>
  intensityFilter.map((intensity) => ({
    text: `Level ${intensity.level}`,
    meta: intensity.level
  }))

const otherLabels = (otherFilter) =>
  otherFilter.map((filter) => ({
    text: filter.title,
    meta: filter.slug
  }))

const equipmentFocusAreaLabels = (filterList) =>
  filterList.map((filter) => ({
    text: filter,
    meta: filter
  }))

export function convertFilterByFieldToObject(
  allFilterOptions,
  filterByField,
  filteredFields
) {
  return allFilterOptions.filter((filter) =>
    filteredFields.includes(filter[filterByField])
  )
}

export function createFilteredStylesObj(categoryOptions, filtered) {
  let stylesObj = {}
  filtered.forEach((filteredCategorySlug) => {
    const category = categoryOptions.find(
      (category) => category.slug === filteredCategorySlug
    )
    const isSubcategory = filteredCategorySlug.indexOf('_') !== -1

    let rootSlug
    let title
    let slug
    let filteredSubcategories

    if (isSubcategory) {
      rootSlug = filteredCategorySlug.split('_')[0]

      const rootCategory = categoryOptions.find(
        (category) => category.slug === rootSlug
      )

      const subcategoryObj = rootCategory?.subcategories?.find(
        (subcategory) => subcategory.slug === filteredCategorySlug
      )

      title = rootCategory?.title
      slug = rootCategory?.slug
      filteredSubcategories = [subcategoryObj]
    } else {
      title = category?.title
      slug = category?.slug
      filteredSubcategories = [{ title, slug }]
      rootSlug = slug
    }
    if (stylesObj[rootSlug]) {
      stylesObj[rootSlug].filteredSubcategories = [
        ...stylesObj[rootSlug].filteredSubcategories,
        ...filteredSubcategories
      ]
    } else if (rootSlug) {
      stylesObj[rootSlug] = {
        title,
        slug,
        filteredSubcategories
      }
    }
  })
  return stylesObj
}

// Checks for if selected option has already been filtered

export function isCategoryAlreadyFiltered(
  filteredRootCategories,
  root,
  isRoot,
  subcategory
) {
  const rootCategoryObj = filteredRootCategories[root.slug]
  let alreadyFiltered

  if (rootCategoryObj) {
    const filteredSubcategories = rootCategoryObj.filteredSubcategories

    if (isRoot) {
      alreadyFiltered = filteredSubcategories === 'all'
    } else {
      alreadyFiltered =
        filteredSubcategories !== 'all' &&
        filteredSubcategories
          .map((subcategory) => subcategory.slug)
          .includes(subcategory.slug)
    }
  }
  return alreadyFiltered
}

export function getDifficultyIcon(difficulty) {
  switch (difficulty) {
    case 'introductory':
      return <IntroductoryIcon />
    case 'beginner':
      return <BeginnerIcon />
    case 'moderate':
      return <ModerateIcon />
    case 'intermediate':
      return <IntermediateIcon />
    case 'advanced':
      return <AdvancedIcon />
  }
}

export function getIntensityIcon(intensity) {
  switch (Number(intensity)) {
    case 1:
      return <IntensityOneIcon />
    case 2:
      return <IntensityTwoIcon />
    case 3:
      return <IntensityThreeIcon />
    case 4:
      return <IntensityFourIcon />
  }
}

export function getTotalNumberOfFilters(filteredLabels) {
  let count = 0
  for (const filter in filteredLabels) {
    const filteredItem = filteredLabels[filter]
    if (filteredItem?.length > 0) {
      count += filteredItem?.length
    }
  }
  return count
}
